export interface DocumentCreator {
  id: string
  first_name: string
  last_name: string
  middle_name?: string
  avatar?: string
  department_name?: string
  position_name?: string
}

export interface DocumentType {
  id: number
  name: string
  slug?: string
  description?: string | null
  tags?: string[]
  created_at?: string
}

export interface DocumentCategory {
  id: number
  name: string
}

export interface DocumentScope {
  id: number
  name: string
}

export interface DocumentTypeWritePayload {
  name: string
  slug?: string
  description?: string
  tags?: string[]
}

export interface FetchDocumentTypesParams {
  search?: string
  ordering?: string
  page?: number
  page_size?: number
  cursor?: string
}

export interface DocumentSection {
  id: string
  name?: string
  label?: string
}

export interface DocumentFolder {
  id: string
  name: string
}

export interface DocumentVersion {
  id: number
  document?: string
  edition?: string
  version?: string
  file: string
  filename?: string
  size?: number
  extension?: string
  description?: string
  status?: string
  approved_at?: string | null
  valid_until?: string | null
  effective_at?: string | null
  created_by?: string
  created_at: string
}

export interface DocumentEdition {
  id: number
  edition: string
  file: string
  filename?: string
  effective_at?: string | null
  approved_at?: string | null
  valid_until?: string | null
  created_by?: string
  created_at: string
}

export const getDocumentEdition = (item: Pick<DocumentVersion, 'edition' | 'version'>) =>
  item.edition ?? item.version ?? ''

export interface AccessEntity {
  id: string
  name?: string
  short_name?: string
}

export type DocumentStatusSlug = 'active' | 'archived' | 'draft' | 'cancelled' | 'canceled' | 'archive'

export interface DocumentDetails {
  id: string
  name: string
  number?: string
  type?: DocumentType
  section?: DocumentSection
  sections?: DocumentSection[]
  folder?: DocumentFolder
  status?: string
  download_url?: string
  global_access?: boolean
  department_access?: AccessEntity[]
  position_access?: AccessEntity[]
  author?: DocumentCreator | null
  authors?: DocumentCreator[]
  created_by?: DocumentCreator
  created_at: string
  updated_at?: string
  effective_at?: string | null
  approved_at?: string | null
  valid_until?: string | null
  versions: DocumentVersion[]
  employees?: DocumentCreator[]
  employee_access?: DocumentCreator[]
}

export interface SearchTextPart {
  text: string
  highlight: boolean
}

export type DocumentAudioKind = 'summary' | 'full'
export interface DocumentText {
  version_id: number | null
  edition: string | null
  kind: DocumentAudioKind
  text: string
}
export interface DocumentAudioTrack {
  kind: DocumentAudioKind
  generation: string | null
  status: 'unavailable' | 'not_started' | 'waiting' | 'queued' | 'generating' | 'ready' | 'failed' | 'stale'
  reason: string | null
  file: { duration: number; url: string } | null
}
export interface DocumentAudioManifest {
  version_id: number | null
  tracks: DocumentAudioTrack[]
}

export interface DocumentItem {
  id: string
  name: string
  number?: string
  edition?: string
  version?: string
  type?: DocumentType
  section?: DocumentSection
  sections_content?: string
  folder?: DocumentFolder
  status: string
  status_name?: string
  download_url?: string
  author?: DocumentCreator | null
  authors?: DocumentCreator[]
  created_by: DocumentCreator
  approved_at: string | null
  valid_until: string | null
  effective_at?: string | null
  created_at: string
  search_name_parts?: SearchTextPart[]
  search_number_parts?: SearchTextPart[]
  search_context_parts?: SearchTextPart[]
  search_context_source?: 'content' | 'description' | 'semantic' | null
  search_source?: string
  search_score?: number | string
  is_dir: boolean
}

export interface DocumentsResponse {
  count: number
  next: string | null
  previous: string | null
  results: DocumentItem[]
}

export interface FetchDocumentsParams {
  categories?: number[]
  created_after?: string
  created_before?: string
  approved_after?: string
  approved_before?: string
  valid_until_after?: string
  valid_until_before?: string
  created_by?: string[]
  authors?: string[]
  cursor?: string
  folder?: string
  numbers?: string
  page?: number
  page_size?: number
  scopes?: number[]
  search?: string
  sections?: string[]
  /** local — портал; global — документы из daomed_main */
  source?: 'local' | 'global'
  sort?: string
  status?: string[]
  types?: Array<string | number>
  /** true — только действующие документы (переключатель «Скрыть недействующие»), не задан — все */
  is_actual?: boolean
}

export interface Department {
  id: string
  name: string
  short_name: string
  children?: Department[]
}

export interface DepartmentsResponse {
  count: number
  next: string | null
  previous: string | null
  results: Department[]
}

export interface PositionsResponse {
  count: number
  next: string | null
  previous: string | null
  results: Position[]
}

export interface Position {
  id: string
  name: string
}

export interface UpdateDocumentPayload {
  number?: string
  name: string
  author_ids?: string[]
  global_access: boolean
  department_access?: { id: string }[]
  position_access?: { id: string }[]
  employee_access?: { id: string }[]
  type_id: number
  folder_id?: string
  sections?: { id: string }[]
  section_id?: string
  description?: string
}

export interface CreateDocumentPayload {
  number: string
  name: string
  author_ids?: string[]
  sections: { id: string }[]
  global_access: boolean
  department_access: { id: string }[]
  position_access: { id: string }[]
  employee_access: { id: string }[]
  type_id: number
  folder_id: string
}

export type DocumentAcknowledgementStatus = 'pending' | 'acknowledged'

export interface DocumentAcknowledgementItem {
  id: number
  employeeId?: string | null
  employeeName: string
  departmentName: string
  acknowledgedAt: string | null
  status: DocumentAcknowledgementStatus
  actions?: string[]
}

/** Запись ознакомления / задачи из nested responsibility-entries документа */
export interface DocumentResponsibilityEntry {
  id: number
  number?: number
  type?: string
  status?: string
  responsible_employee?: {
    id: string
    first_name?: string
    last_name?: string
    middle_name?: string
    department_name?: string | null
    position_name?: string | null
  } | null
  created_at?: string
  updated_at?: string
  read_at?: string | null
  actions?: string[]
}

export interface AssignDocumentFamiliarizersPayload {
  responsible_employee_ids: string[]
}
